import React, { useEffect, useState } from 'react';
import { LatLng, LatLngExpression, Map } from 'leaflet';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Pane,
  Tooltip,
  useMapEvent,
  Circle,
  Popup,
} from 'react-leaflet';
import { OBSTACLE_COLORS } from 'views/RoutePlanner/ObstaclePlanner';
import { indexPriority, geofenceColor } from '../../../utils/ui';
import { DropLocation } from '@m4a3/api-client';
import markerIconPng from "leaflet/dist/images/marker-icon.png"
import L from 'leaflet';
import { getOsmObstacleDataForCoordinates } from '../../../actions/obstacleActions';
import { FeatureCollection } from 'geojson';
import { obstacleData } from '../../Obstacles/MBObstacles';
import { dropPointRadiusInMetres } from '../../../constants';
import { DraggableCircle } from '../../RoutePlanner/components/DraggableCircle';
import axios from 'axios';
import { getLatestJwtToken } from 'services/amplify-auth';
import { async } from 'validate.js';
import { validateDropLocation } from '../../../actions/adminActions';

interface SuggestedDropPointConfirmationMapProps {
  intialCoords: LatLngExpression;
  mapRef: React.MutableRefObject<Map>;
  obstacles: any;
  dropPoints: DropLocation[];
  geofences: any;
  eircodeCoords: [number, number];
  highlightOrderId: number;
  onChangeSuggestedDrops: (suggestedDrops: { latLng: LatLng, isEvaluated: boolean; isLowRisk: boolean }[]) => void;

  loadingRiskEvaluation?: boolean;
}

type SuggestedDropPoint = {
  latLng: LatLng;
  isEvaluated: boolean;
  isLowRisk: boolean;
}

export function SuggestedDropPointConfirmationMap({
  intialCoords,
  mapRef,
  obstacles,
  dropPoints,
  geofences,
  eircodeCoords,
  highlightOrderId,
  onChangeSuggestedDrops,
  loadingRiskEvaluation = false,
}: SuggestedDropPointConfirmationMapProps) {
  const [suggestedDropLatLngs, setSuggestedDropLatLngs] = useState<SuggestedDropPoint[]>([]);
  const [osmObstacleData, setOsmObstacleData] = useState<FeatureCollection>();

  useEffect(() => {
    getOsmObstacleDataForCoordinates(eircodeCoords[0], eircodeCoords[1])
      .then(data => {
        setOsmObstacleData(data)
      })
  }, [eircodeCoords]);
  const resetSuggestedDropPointsDropPoint = (e): void => {
    e.stopPropagation()
    e.preventDefault()
    setSuggestedDropLatLngs([]);
    onChangeSuggestedDrops([]);
  };

  const getRiskColour = (isEvaluated: boolean, isLowRisk: boolean) => {
    if (!isEvaluated) {
      return 'orange';
    }
    return isLowRisk ? '#0583FF' : 'red';
  }

  const validateDrop = async (e, maxSuggestions, overlappingDropPontIndex, currentDropCoordinates ) => { 

    
      if (suggestedDropLatLngs.length < maxSuggestions && overlappingDropPontIndex === -1) {
        const newSuggestedDrops = [
          ...suggestedDropLatLngs, 
          { latLng: currentDropCoordinates, isEvaluated: false, isLowRisk: false } 
        ];
        setSuggestedDropLatLngs(newSuggestedDrops);
        onChangeSuggestedDrops(newSuggestedDrops);
      } else {
        const newSuggestedDrops = suggestedDropLatLngs.filter(
          (drop, i) => 
            !drop.latLng.equals(e.latlng) && 
            mapRef.current.distance(e.latlng, [drop.latLng.lat, drop.latLng.lng]) > dropPointRadiusInMetres
        );
        setSuggestedDropLatLngs(newSuggestedDrops);
        onChangeSuggestedDrops(newSuggestedDrops);
      }
  }

  function SuggestedDrops() {
    useMapEvent('click', (e) => {
      const maxSuggestions = 2;
      const currentDropCoordinates = e.latlng;
      const overlappingDropPontIndex = suggestedDropLatLngs.findIndex((drop, i) => 
        drop.latLng.equals(e.latlng) || mapRef.current.distance(e.latlng, [drop.latLng.lat, drop.latLng.lng]) <= dropPointRadiusInMetres
      );

      validateDrop(e, maxSuggestions, overlappingDropPontIndex, currentDropCoordinates);
      
    });

    return null;
  }

  return (
    <>
    {mapRef.current && suggestedDropLatLngs.length > 0 ? <button
        type="button"
        onClick={resetSuggestedDropPointsDropPoint}
        className="z-2000 absolute top-3 left-14 rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      >
        Clear
      </button> : <></>}
      <MapContainer
      style={{
        height: '100%',
        width: '100%',
      }}
      whenReady={function() {
        // zoom prop is not changing the initial zoom past 17, so this is the workaround.
        this.setZoom(21);
      }}
      center={eircodeCoords ? eircodeCoords : intialCoords}
      ref={mapRef}
      zoom={17}>
      <SuggestedDrops />
      <TileLayer
        attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <TileLayer
        attribution="Manna"
        url="https://tiles.manna.aero/{z}/{x}/{y}.png"
        maxZoom={24}
      />

      {obstacles?.map(ob => {
        const color = OBSTACLE_COLORS[ob.groundRisk] || 'red';
        return (
          <GeoJSON
            data={ob.geoJson}
            style={{
              color,
              weight: 2,
              fillOpacity: 0.5
            }}
            key={Math.random() + 'xxxgeeeituerijfhy7er'}>
            <Popup>
              <div>
                <strong>Type:</strong> {obstacleData[ob.groundRisk].key}<br/>
                {ob.name && <>
                  <strong>Name:</strong> {ob.name}<br/>
                </>}
              </div>
            </Popup>
        </GeoJSON>);
      })}
      {osmObstacleData?.features?.map(feature => {
        if (feature.geometry.type?.toLowerCase() === 'point') {
          return <Marker
            key={`point-marker-${(feature.geometry as any).coordinates}-${feature.properties.name || feature.properties.type}`}
            position={(feature as any).geometry.coordinates?.reverse()}
            icon={new L.Icon({
              iconUrl: markerIconPng,
              iconSize: [25, 41],
            })}>
            <Tooltip
              permanent
              direction="bottom"
              opacity={0.6}
              offset={[0, 10]}>
              {feature.properties.name || feature.properties.type}
            </Tooltip>
          </Marker>
          } else {
            return (<GeoJSON
              data={feature}
              style={{
                weight: 2,
                fillOpacity: 0.1,
                color: 'red'
              }}
              key={Math.random() + 'osm-feature-xxxgeeeituerijfhy7'}>
              <Popup>
                <div>
                  <strong>Type:</strong> {feature.properties.type?.replace(/_/g, ' ')}<br/>
                  {feature.properties.name && <>
                    <strong>Name:</strong> {feature.properties.name}<br/>
                  </>}
                </div>
              </Popup>
            </GeoJSON>);
        }
      })}
      <Pane name="RoutePane" style={{ zIndex: 10000 }}>
        <Pane
          key="Geofences"
          name="Geofences"
          style={{ zIndex: indexPriority(9) }}>
          {geofences?.length > 0 &&
            geofences.filter(geofence => geofence.active === true)
              .map(geofence => (<GeoJSON
                  data={geofence.geoJson}
                  style={{ fill: false, color: geofenceColor(geofence.id) }}
                  key={geofence.id}
                />
              ))}
        </Pane>
      </Pane>
      <Pane style={{ minWidth: '25px' }} name={`eircode-${eircodeCoords.join(',')}`}>
        <Marker
          position={eircodeCoords}
          icon={new L.Icon({
            iconUrl: markerIconPng,
            iconSize: [25, 41],

          })}>
          <Tooltip
            permanent
            direction="bottom"
            opacity={0.6}
            offset={[0, 10]}>
            {'Eircode'}
          </Tooltip>
        </Marker>
      </Pane>
      {mapRef.current && suggestedDropLatLngs.map((drop, index) => {
        const { latLng, isEvaluated, isLowRisk } = drop;
        return (
          <Pane name={`suggested-drops-${latLng.toString()}`} key={`suggested-drops-${latLng.toString()}`}>
            <DraggableCircle
              center={[latLng.lat, latLng.lng]}
              radius={dropPointRadiusInMetres / 2}
              index={index} 
              onDragEnd={({ lat, lng, index }) => { 
                const updatedSuggestedDrops = [...suggestedDropLatLngs];
                    updatedSuggestedDrops[index] = { ...drop, latLng: new LatLng(lat, lng), isEvaluated: false };
                    setSuggestedDropLatLngs(updatedSuggestedDrops);
                    onChangeSuggestedDrops(updatedSuggestedDrops);
              }}
              pathOptions={{ color: getRiskColour(isEvaluated, isLowRisk) }}
            />
          </Pane>
        );
      })}
      {mapRef.current && dropPoints?.map(dropPoint => {
        return (<Pane name={`old-drop-point-${dropPoint?.id}`} key={`old-drop-point-${dropPoint?.id}`}>
          <Circle
            center={[dropPoint?.latitude, dropPoint?.longitude]}
            radius={dropPointRadiusInMetres / 2}
            fillOpacity={highlightOrderId === dropPoint.id ? 0.7 : 0.2}
            pathOptions={{color: highlightOrderId === dropPoint.id ? '#d97706': '#899499'}}>
          </Circle>
        </Pane>)
      })}
    </MapContainer>
    </>
  );
}